import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  ChartBarIcon, 
  PhotoIcon, 
  UserIcon,
  DocumentArrowDownIcon,
  Cog6ToothIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { imageApi, downloadBlob } from '../services/api'
import toast from 'react-hot-toast'

interface LayoutProps {
  children: ReactNode
  annotatorId: string
  onAnnotatorIdChange: (id: string) => void
  searchTerm?: string
  statusFilter?: string
  projectFilter?: string
  currentSpace?: 'annotator' | 'approver'
  onSpaceChange?: (space: 'annotator' | 'approver') => void
  approverStatusFilter?: string
}

const Layout = ({ children, annotatorId, onAnnotatorIdChange, searchTerm, statusFilter, projectFilter, currentSpace = 'annotator', onSpaceChange, approverStatusFilter }: LayoutProps) => {
  const location = useLocation()
  const [isExporting, setIsExporting] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [isEditingAnnotator, setIsEditingAnnotator] = useState(false)
  const [tempAnnotatorId, setTempAnnotatorId] = useState(annotatorId)

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const blob = await imageApi.exportAnnotations(searchTerm, statusFilter, projectFilter, currentSpace, approverStatusFilter)
      const filename = `annotations_export_${currentSpace}_${new Date().toISOString().split('T')[0]}.csv`
      downloadBlob(blob, filename)
      toast.success('Annotations exported successfully!')
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export annotations')
    } finally {
      setIsExporting(false)
    }
  }

  const handleDownloadImages = async () => {
    try {
      setIsDownloading(true)
      const blob = await imageApi.downloadImages(searchTerm, statusFilter, projectFilter, currentSpace, approverStatusFilter)
      const filename = `images_download_${currentSpace}_${new Date().toISOString().split('T')[0]}.zip`
      downloadBlob(blob, filename)
      toast.success('Images downloaded successfully!')
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Failed to download images')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleCompleteAnnotations = async () => {
    try {
      setIsCompleting(true)
      let result
      
      if (currentSpace === 'approver') {
        // Use approver complete endpoint
        result = await imageApi.completeApproverAnnotations(searchTerm, approverStatusFilter, projectFilter)
      } else {
        // Use annotator complete endpoint
        result = await imageApi.completeAnnotations(searchTerm, statusFilter, projectFilter)
      }
      
      toast.success(result.message || 'Annotations completed successfully')
      // Optionally refresh the page or emit an event to refresh data
      window.location.reload()
    } catch (error: any) {
      console.error('Complete annotations failed:', error)
      const errorMessage = error.response?.data?.detail || 'Failed to complete annotations'
      toast.error(errorMessage)
    } finally {
      setIsCompleting(false)
    }
  }

  const handleAnnotatorIdSave = () => {
    if (tempAnnotatorId.trim()) {
      onAnnotatorIdChange(tempAnnotatorId.trim())
      setIsEditingAnnotator(false)
      toast.success('Annotator ID updated')
    }
  }

  const handleAnnotatorIdCancel = () => {
    setTempAnnotatorId(annotatorId)
    setIsEditingAnnotator(false)
  }

  const navigation = [
    {
      name: 'Image Annotation',
      href: '/',
      icon: PhotoIcon,
      current: location.pathname === '/'
    },
    {
      name: 'Statistics',
      href: '/stats',
      icon: ChartBarIcon,
      current: location.pathname === '/stats'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and main navigation */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  Image Annotation System
                </h1>
              </div>
              
              {/* Space Toggle - only show on annotation page */}
              {location.pathname === '/' && (
                <div className="ml-6 flex items-center">
                  <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => onSpaceChange?.('annotator')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        currentSpace === 'annotator'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Annotator
                    </button>
                    <button
                      onClick={() => onSpaceChange?.('approver')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        currentSpace === 'approver'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Approver
                    </button>
                  </div>
                </div>
              )}
              
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        item.current
                          ? 'border-blue-500 text-gray-900 dark:text-white'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                    >
                      <Icon className="w-5 h-5 mr-2" />
                      {item.name}
                    </Link>
                  )
                })}
              </div>
            </div>

            {/* Right side actions */}
            <div className="flex items-center space-x-4">
              {/* Complete Annotation button - show for specific conditions based on space */}
              {((currentSpace === 'annotator' && (statusFilter === 'accepted' || statusFilter === 'rejected' || statusFilter === 'regenerate')) ||
                (currentSpace === 'approver')) ? (
                <button
                  onClick={handleCompleteAnnotations}
                  disabled={isCompleting}
                  className="btn btn-primary"
                >
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  {isCompleting ? 'Completing...' : 'Complete Annotation'}
                </button>
              ) : null}

              {/* Export CSV button - only show in approver space */}
              {currentSpace === 'approver' && (
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="btn btn-secondary"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </button>
              )}

              {/* Download Images button - only show in approver space */}
              {currentSpace === 'approver' && (
                <button
                  onClick={handleDownloadImages}
                  disabled={isDownloading}
                  className="btn btn-secondary"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                  {isDownloading ? 'Downloading...' : 'Download Images'}
                </button>
              )}

              {/* Annotator ID */}
              <div className="flex items-center space-x-2">
                <UserIcon className="w-5 h-5 text-gray-400" />
                {isEditingAnnotator ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={tempAnnotatorId}
                      onChange={(e) => setTempAnnotatorId(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Annotator ID"
                      onKeyPress={(e) => e.key === 'Enter' && handleAnnotatorIdSave()}
                    />
                    <button
                      onClick={handleAnnotatorIdSave}
                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleAnnotatorIdCancel}
                      className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {annotatorId}
                    </span>
                    <button
                      onClick={() => setIsEditingAnnotator(true)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      <Cog6ToothIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile navigation */}
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    item.current
                      ? 'bg-blue-50 dark:bg-blue-900 border-blue-500 text-blue-700 dark:text-blue-200'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  )
}

export default Layout 